import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, MessageCircleHeart, CheckCircle2 } from 'lucide-react';
import PIELayout from './PIELayout';
import PIESkeleton from './PIESkeleton';
import { pieApi, pieCoachPreseed } from './pieApi';
import PIEUnseededEmptyState, { isUnseededError } from './PIEUnseededEmptyState';
import { pct, pp } from './format';
import { Perf } from './PIEDisplayMode';
import PIECalibrationBadge from './PIECalibrationBadge';
import './pie.css';
import logger from '../../lib/logger';

/**
 * PIESimulatorPage — "What Happens If I Do This?"
 * Modern centered hero, action grid, animated result panel below.
 */
export default function PIESimulatorPage() {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [loadingActions, setLoadingActions] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    pieApi
      .listActions()
      .then((d) => setActions(d.actions || []))
      .catch((e) => {
        logger.error('listActions failed', e);
        setErr(e?.response?.data?.detail || 'Could not load actions');
      })
      .finally(() => setLoadingActions(false));
  }, []);

  const onSimulate = async (actionId) => {
    setSelected(actionId);
    setRunning(true);
    setResult(null);
    setCommitted(null);
    try {
      const r = await pieApi.simulate(actionId);
      setResult(r);
    } catch (e) {
      logger.error('simulate failed', e);
      setErr(e?.response?.data?.detail || 'Simulation failed');
    } finally {
      setRunning(false);
    }
  };

  const onCommit = async () => {
    if (!selected || committing) return;
    setCommitting(true);
    try {
      const c = await pieApi.createCommitment(selected);
      setCommitted(c);
    } catch (e) {
      logger.error('commit failed', e);
      setErr(e?.response?.data?.detail || 'Could not save commitment');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <PIELayout>
      {isUnseededError(err) ? <PIEUnseededEmptyState /> : (
      <div className="pie-fade-in" data-testid="pie-simulator">
        <section className="pie-hero-center">
          <span className="pie-eyebrow">
            <Sparkles size={12} /> What if
          </span>
          <h1 className="pie-hero-h1">
            Pick one. <span className="pie-accent-text">See what shifts.</span>
          </h1>
          <p className="pie-hero-sub">
            Each action runs through the same impact engine our org dashboards use —
            same KPI weights, same dampening, same hard 3% ceiling per KPI to keep
            things honest. The math is bounded; it won&apos;t over-promise.
          </p>
        </section>

        {loadingActions && (
          <div
            data-testid="pie-simulator-skeleton"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 12,
              marginBottom: 40,
            }}
          >
            {Array.from({ length: 6 }, (_, i) => (
              <PIESkeleton key={i} variant="card" height={88} />
            ))}
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 12,
            marginBottom: 40,
          }}
          data-testid="pie-action-grid"
        >
          {actions.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`pie-action-chip ${selected === a.id ? 'pie-action-chip-active' : ''}`}
              onClick={() => onSimulate(a.id)}
              disabled={running}
              data-testid={`pie-action-${a.id}`}
            >
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--pie-text)' }}>{a.label}</span>
              <span style={{ fontSize: 12, color: 'var(--pie-text-muted)' }}>
                ~{formatEffort(a.effort_minutes)}
              </span>
            </button>
          ))}
        </div>

        {running && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--pie-text-muted)' }}>
            <div className="pie-loader"><span /><span /><span /></div>
            <div style={{ marginTop: 12, fontSize: 13 }}>Running through the impact engine…</div>
          </div>
        )}

        {result && !running && (
          <ResultPanel
            result={result}
            committing={committing}
            committed={committed}
            onCommit={onCommit}
            onGoValidate={() => navigate('/mirror/validate')}
            onTalkOver={() => {
              const seed =
                `I just simulated "${result.action.label}". ` +
                `It would shift my PE from ${pct(result.before.pe)} to ${pct(result.after.pe)} ` +
                `(predicted ROE ${(result.roe.adjusted * 100).toFixed(0)}% — ${result.roe.band}). ` +
                "Honestly, do you think this is the right move for me right now, or is there something better?";
              pieCoachPreseed.set(seed);
              navigate('/mirror/coach');
            }}
          />
        )}

        {err && (
          <div
            className="pie-card"
            style={{ marginTop: 24, color: 'var(--pie-danger)' }}
            data-testid="pie-simulator-error"
          >
            {err}
          </div>
        )}
      </div>
      )}
    </PIELayout>
  );
}

function ResultPanel({ result, committing, committed, onCommit, onGoValidate, onTalkOver }) {
  const { action, before, after, roe, kpi_impacts, calibration } = result;

  return (
    <div className="pie-fade-in" data-testid="pie-simulator-result">
      <div
        className="pie-card pie-card-elevated"
        style={{ marginBottom: 16, textAlign: 'center', padding: '32px 28px' }}
      >
        <span className="pie-eyebrow" style={{ marginBottom: 14 }}>If you do this</span>
        <h2 className="pie-display-md" style={{ marginBottom: 6 }}>{action.label}</h2>
        <span className="pie-chip">Effort: about {formatEffort(action.effort_minutes)}</span>
      </div>

      <div className="pie-scorerow" style={{ marginBottom: 16 }}>
        <DeltaCard label="Capability (C)" before={before.c} after={after.c} delta={after.delta_c} />
        <DeltaCard label="Motivation (M)" before={before.m} after={after.m} delta={after.delta_m} />
        <DeltaCard label="PE (C × M)"      before={before.pe} after={after.pe} delta={after.delta_pe} />
      </div>

      <div className="pie-card" style={{ marginBottom: 16 }} data-testid="pie-roe-result">
        <div className="pie-score-eyebrow" style={{ marginBottom: 14 }}>Predicted ROE (transfer factor)</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
          <span className="pie-display-lg pie-mono">{(roe.adjusted * 100).toFixed(0)}%</span>
          <span
            className="pie-chip"
            style={{
              background: bandBgColor(roe.band),
              color: bandTextColor(roe.band),
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: 11,
            }}
            data-testid="pie-roe-band"
          >
            {roe.band}
          </span>
          <PIECalibrationBadge calibration={calibration} />
        </div>
        <p style={{ fontSize: 14, color: 'var(--pie-text-secondary)', marginTop: 14, lineHeight: 1.6 }}>
          ROE captures how much of your learning actually transfers to behavior change on the job.
          Anything below 0.30 means most of the value isn&apos;t landing — usually because of motivation
          or manager-support gaps.
        </p>
      </div>

      <div className="pie-card" data-testid="pie-kpi-impact-table">
        <div className="pie-score-eyebrow" style={{ marginBottom: 14 }}>Top KPI impact (per FTE in your role)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--pie-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <th style={{ padding: '8px 0', fontWeight: 600 }}>KPI</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600 }}>Impact %</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600 }}>$ / FTE</th>
            </tr>
          </thead>
          <tbody>
            {kpi_impacts.map((k) => (
              <tr key={k.kpi_id} style={{ borderTop: '1px solid var(--pie-border)' }}>
                <td style={{ padding: '12px 0', color: 'var(--pie-text)' }}>{k.kpi_name}</td>
                <td className="pie-mono" style={{ padding: '12px 0', textAlign: 'right' }}>{k.impact_pct.toFixed(2)}%</td>
                <td className="pie-mono" style={{ padding: '12px 0', textAlign: 'right', fontWeight: 600, color: 'var(--pie-text)' }}>
                  ${Math.round(k.value_per_employee).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        {!committed ? (
          <button
            type="button"
            className="pie-btn pie-btn-primary"
            onClick={onCommit}
            disabled={committing}
            data-testid="pie-result-cta-commit"
          >
            <CheckCircle2 size={14} />
            {committing ? 'Saving…' : 'Commit to this'}
            <ArrowRight size={14} />
          </button>
        ) : (
          <button
            type="button"
            className="pie-btn pie-btn-accent"
            onClick={onGoValidate}
            data-testid="pie-result-cta-go-validate"
          >
            <CheckCircle2 size={14} /> Saved — go to “Did it work?”
            <ArrowRight size={14} />
          </button>
        )}
        <Link to="/mirror/coach" className="pie-btn pie-btn-ghost" data-testid="pie-result-cta-coach" onClick={(e) => { e.preventDefault(); onTalkOver(); }}>
          <MessageCircleHeart size={14} /> Talk this over with my coach
        </Link>
      </div>
    </div>
  );
}

function DeltaCard({ label, before, after, delta }) {
  const positive = delta > 0;
  const cls = positive ? 'pie-delta-positive' : delta < 0 ? 'pie-delta-negative' : 'pie-delta-neutral';
  return (
    <div className="pie-score-card">
      <div className="pie-score-eyebrow">{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 14 }}>
        <span className="pie-display-md"><Perf value={after || 0} kind="pct01" /></span>
        <span className={cls} style={{ fontSize: 14 }}>
          {positive ? '↑' : delta < 0 ? '↓' : '→'} <Perf value={delta} kind="delta01" digits={1} signed={false} />
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--pie-text-muted)', marginTop: 8 }}>
        was <Perf value={before || 0} kind="pct01" />
      </div>
    </div>
  );
}

function bandBgColor(band) {
  switch ((band || '').toLowerCase()) {
    case 'high':     return '#D1FAE5';
    case 'moderate': return '#CFFAFE';
    case 'low':      return '#FEF3C7';
    case 'minimal':  return '#FEE2E2';
    default:         return '#F1F5F9';
  }
}
function bandTextColor(band) {
  switch ((band || '').toLowerCase()) {
    case 'high':     return '#047857';
    case 'moderate': return '#0891B2';
    case 'low':      return '#B45309';
    case 'minimal':  return '#B91C1C';
    default:         return '#475569';
  }
}

function formatEffort(mins) {
  if (mins < 60) return `${mins} min`;
  const hours = mins / 60;
  if (hours < 8) return `${hours.toFixed(1)} hours`;
  return `${Math.round(hours)} hours total`;
}
